import { IElement, OneOfElements } from '@shared/models/project.model';
import { projectGuards } from '../../guards/project-guards.helpers';
import {
  clearRecipe,
  detachRecipe,
  overwriteRecipe,
  overwriteRecipeForProposal,
} from '../../recipe.helpers';
import { isRedundantElementRecipeApplyChange } from '../../recipe-apply-payload.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';

type ElementRecipeApplyChange =
  ProjectChangeMap[ProjectChangeType.ElementRecipeApply];
type ElementRecipeDetachChange =
  ProjectChangeMap[ProjectChangeType.ElementRecipeDetach];
type ElementRecipeClearChange =
  ProjectChangeMap[ProjectChangeType.ElementRecipeClear];

/**
 * Apply recipe structurally for the current element and the change payload recipe.
 *
 * Picks the overwrite strategy from {@link ElementRecipeApplyChange.overwriteMode}:
 * `'proposal'` (default) keeps baseline-only properties the new recipe omits via
 * {@link overwriteRecipeForProposal} (proposal replay and per-proposal swaps, DEV-3293/DEV-3350);
 * `'baseline'` clears element-only state via {@link overwriteRecipe} for direct baseline applies.
 * When the change carries a regenerated {@link ElementRecipeApplyChange.fallbackName} it is persisted too.
 *
 * @param element - Current element from the project tree
 * @param change - Target recipe, overwrite mode, and optional fallback name
 * @returns Partial with recipe_id, properties, elements (and fallbackName when provided)
 */
export const elementRecipeApplyToChange = (
  element: OneOfElements,
  change: ElementRecipeApplyChange,
): Partial<IElement> | undefined => {
  if (!projectGuards.is.element(element)) return undefined;

  // Re-applying the recipe the element already has is a no-op (baseline and proposal), unless its
  // children were never materialized — then the apply must run to rebuild them.
  if (isRedundantElementRecipeApplyChange(element, change)) return undefined;

  const overwritten =
    change.overwriteMode === 'baseline'
      ? overwriteRecipe(element, change.recipe)
      : overwriteRecipeForProposal(element, change.recipe);

  const { recipe_id, properties, elements } = overwritten;
  const partial: Partial<IElement> = { recipe_id, properties, elements };

  // Persist the regenerated fallback name so the structural apply also carries the rename.
  if (change.fallbackName !== undefined) {
    partial.fallbackName = change.fallbackName;
  }

  return partial;
};

/**
 * Detach recipe link while keeping recipe-sourced properties and children.
 *
 * @param element - Current element state
 * @param change - Detach change payload
 * @returns Partial element or undefined when not an {@link ElementKind.Element}
 */
export const elementRecipeDetachToChange = (
  element: OneOfElements,
  _change: ElementRecipeDetachChange,
): Partial<IElement> | undefined => {
  if (!projectGuards.is.element(element)) return undefined;

  return detachRecipe(element);
};

/**
 * Clear recipe link, recipe properties, and recipe children.
 *
 * @param element - Current element state
 * @param change - Clear change payload
 * @returns Partial element or undefined when not an {@link ElementKind.Element}
 */
export const elementRecipeClearToChange = (
  element: OneOfElements,
  _change: ElementRecipeClearChange,
): Partial<IElement> | undefined => {
  if (!projectGuards.is.element(element)) return undefined;

  return clearRecipe(element);
};
